import { trackEvent } from "@/lib/audit";
import { getMyStreak } from "@/lib/dailyStreak.functions";

const KEY = "ss2026_streak";

export interface StreakState {
  consecutive_days_active: number;
  longest_streak: number;
  last_active_date: string | null;
  streak_rewards_claimed: number[];
}

function todayKey() { return new Date().toISOString().slice(0, 10); }
function yesterdayKey() {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

export function loadStreak(): StreakState {
  if (typeof window === "undefined") {
    return { consecutive_days_active: 0, longest_streak: 0, last_active_date: null, streak_rewards_claimed: [] };
  }
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { consecutive_days_active: 0, longest_streak: 0, last_active_date: null, streak_rewards_claimed: [] };
    return JSON.parse(raw) as StreakState;
  } catch {
    return { consecutive_days_active: 0, longest_streak: 0, last_active_date: null, streak_rewards_claimed: [] };
  }
}

export function pingStreak(): StreakState {
  const s = loadStreak();
  const today = todayKey();
  if (s.last_active_date === today) return s;
  const next: StreakState = { ...s };
  if (s.last_active_date === yesterdayKey()) next.consecutive_days_active += 1;
  else next.consecutive_days_active = 1;
  next.last_active_date = today;
  next.longest_streak = Math.max(next.longest_streak, next.consecutive_days_active);
  if (typeof window !== "undefined") localStorage.setItem(KEY, JSON.stringify(next));
  void trackEvent("streak_updated", {
    consecutive: next.consecutive_days_active,
    longest: next.longest_streak,
  });
  return next;
}

export const STREAK_REWARD_DAYS = [3, 7, 14, 30];

export function nextStreakMilestone(state: StreakState): number | null {
  for (const d of STREAK_REWARD_DAYS) if (state.consecutive_days_active < d) return d;
  return null;
}

/**
 * PR-34A — Server-authoritative streak reconciliation.
 * The localStorage streak is treated as a visual cache only. If the server's
 * `daily_streaks.current_streak` differs, the server value wins and overwrites
 * the cache. Safe to call from any client surface that surfaces a streak.
 */
export async function reconcileStreakFromServer(): Promise<StreakState> {
  const local = loadStreak();
  try {
    const server = await getMyStreak();
    if (!server) return local;
    const reconciled: StreakState = {
      consecutive_days_active: server.current_streak ?? 0,
      longest_streak: Math.max(local.longest_streak, server.best_streak ?? 0),
      last_active_date: server.last_claim_at
        ? new Date(server.last_claim_at).toISOString().slice(0, 10)
        : local.last_active_date,
      streak_rewards_claimed: local.streak_rewards_claimed,
    };
    if (
      reconciled.consecutive_days_active !== local.consecutive_days_active ||
      reconciled.longest_streak !== local.longest_streak
    ) {
      if (typeof window !== "undefined") {
        try { localStorage.setItem(KEY, JSON.stringify(reconciled)); } catch { /* noop */ }
      }
      void trackEvent("streak_reconciled_from_server", {
        local_consecutive: local.consecutive_days_active,
        server_consecutive: reconciled.consecutive_days_active,
      });
    }
    return reconciled;
  } catch {
    return local;
  }
}